import { SlideType } from '@breakoutlearning/firebase-repository/types'
import { useDialogs } from 'hooks/dialogs'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { ShowExperienceDialog } from '../dialogs/ShowExperienceDialog'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useTranslationTyped } from 'i18n/i18n'
import { type StudentPeerReviewController } from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'
import { useEffect, useState } from 'react'
import { PeerReviewCompletionDialog } from '../dialogs/PeerReviewCompletionDialog'
import { TriangleAlertFillIcon } from 'components/icons/TriangleAlertFill'
import { reaction } from 'mobx'

export const CompletedViewPeerReviewNeeded = observer(
  function CompletedViewPeerReviewNeeded({
    peerReviewController,
  }: {
    peerReviewController: StudentPeerReviewController
  }) {
    const cubit = useStudentAssignmentCubit()
    const { t, tt } = useTranslationTyped()
    const { showDialog } = useDialogs()

    const summary = cubit.roomStateSummary

    useEffect(() => {
      // these functions are idempotent, repeat calls are no-ops
      // timeout for rules to get ahead
      const t = setTimeout(() => {
        peerReviewController.initializeAsReviewer()
        peerReviewController.initializeAsReviewee()
      }, 100)
      return () => clearTimeout(t)
    }, [peerReviewController])

    useAutoOpenPeerReviewDialog({
      cubit,
      peerReviewController,
    })

    return (
      <div
        className={classNames(
          `mt-1 flex flex-1 flex-col items-center justify-center py-5 text-center`
        )}
      >
        <div className="text-body-medium text-grey-text">
          {t('dart.experience')}
        </div>
        <h2 className="mt-3 text-title-large">
          <TriangleAlertFillIcon
            size={20}
            className="mr-1 inline-block align-top text-core-error"
          />
          {tt.student_assignment.peer_review_pending()}
        </h2>
        <div className="mt-6">
          <div className="flex flex-row flex-wrap justify-center gap-2">
            <BreakoutButton
              onClick={(e) => {
                showDialog(
                  () => (
                    <ShowExperienceDialog
                      slideDeckId={cubit.libraryObject.slideDeckId}
                      sectionId={cubit.sectionId}
                      assignmentId={cubit.assignmentId}
                    />
                  ),
                  { trigger: e.currentTarget }
                )
              }}
              className="inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
            >
              {t('student_assignment.view_experience')}
            </BreakoutButton>
            {summary?.hasData && (
              <BreakoutTooltip
                placement="top"
                content={tt.student_assignment.complete_peer_review_before_summary()}
              >
                <div>
                  <BreakoutButton
                    disabled
                    className="inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
                  >
                    {t('student_assignment.view_summary')}
                  </BreakoutButton>
                </div>
              </BreakoutTooltip>
            )}
            <BreakoutButton
              kind="accent"
              onClick={(e) => {
                showDialog(
                  () => (
                    <PeerReviewCompletionDialog
                      controller={peerReviewController}
                      assignmentTitle={
                        cubit.assignment.slideDeck.data.slideDeckName
                      }
                    />
                  ),
                  { trigger: e.currentTarget }
                )
              }}
              className="ml-1 inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
            >
              {peerReviewController.userNeedsToStartPeerReview.value
                ? tt.student_assignment.start_peer_review()
                : tt.student_assignment.complete_peer_review()}
            </BreakoutButton>
          </div>
        </div>
      </div>
    )
  }
)

function useAutoOpenPeerReviewDialog({
  cubit,
  peerReviewController: controller,
}: {
  cubit: ReturnType<typeof useStudentAssignmentCubit>
  peerReviewController: StudentPeerReviewController
}) {
  const { showDialog } = useDialogs()

  const [sessionProcessingOrEnded, setSessionProcessingOrEnded] =
    useState(false)

  // there is a processing slide, and we are on or past it
  useEffect(() => {
    const disposer = reaction(
      () => ({
        slidesLoaded: cubit.slides.isLoaded,
        roomStateLoaded: cubit.roomState.isLoaded,
        slideDeckLoaded: cubit.slideDeck.isLoaded,
        slides: cubit.slides.models,
        activeSlide: cubit.roomState.data.activeSlide,
      }),
      ({
        slidesLoaded,
        roomStateLoaded,
        slideDeckLoaded,
        slides,
        activeSlide,
      }) => {
        if (!slidesLoaded || !roomStateLoaded || !slideDeckLoaded) return false

        // no active slide means not started
        if (typeof activeSlide !== 'number')
          return setSessionProcessingOrEnded(false)

        const sessionProcessingOrResultsTypes = [
          SlideType.processingData,
          SlideType.sessionResults,
          SlideType.endOfSession,
        ]

        const indexOfFirstSessionProcessingOrResultsSlide = slides.findIndex(
          (s) => sessionProcessingOrResultsTypes.includes(s.data.slideType)
        )

        // i don't think we allow for publishing of these slide decks with no results/processing.
        // if i am wrong though, pop peer review dialog once we reach the last slide
        if (indexOfFirstSessionProcessingOrResultsSlide === -1) {
          return setSessionProcessingOrEnded(activeSlide >= slides.length)
        }

        // we are beyond the processing/results slide
        if (activeSlide >= indexOfFirstSessionProcessingOrResultsSlide)
          return setSessionProcessingOrEnded(true)

        return setSessionProcessingOrEnded(false)
      },
      { fireImmediately: true }
    )
    return () => disposer()
  }, [
    cubit.roomState.data.activeSlide,
    cubit.roomState.isLoaded,
    cubit.slideDeck.isLoaded,
    cubit.slides.isLoaded,
    cubit.slides.models,
  ])

  const userYetToCompletePeerReview = controller.userYetToCompletePeerReview
  const userId = cubit.repository.uid

  useEffect(() => {
    const disposer = reaction(
      () => ({
        roomId: cubit.roomStateId,
        sessionProcessingOrEnded,
        userYetToCompletePeerReview,
        assignmentId: cubit.assignmentId,
        sectionId: cubit.sectionId,
        slideDeckName: cubit.assignment.slideDeck.data.slideDeckName,
      }),
      ({
        roomId,
        sessionProcessingOrEnded,
        userYetToCompletePeerReview,
        assignmentId,
        sectionId,
        slideDeckName,
      }) => {
        // if no room (no key), don't ever pop the dialog
        if (!roomId) return

        const dialogKey = `${roomId}:${userId}:${sectionId}:${assignmentId}`

        // if we don't yet know if user has yet to
        // complete the review, don't do anything
        if (userYetToCompletePeerReview.isLoading) return

        // if user has not completed review
        // or the session
        if (!userYetToCompletePeerReview.value || !sessionProcessingOrEnded) {
          controller.peerReviewCompletionDialogKeys.delete(dialogKey)
          return
        }

        if (!controller.peerReviewCompletionDialogKeys.has(dialogKey)) {
          controller.peerReviewCompletionDialogKeys.add(dialogKey)
          showDialog(() => (
            <PeerReviewCompletionDialog
              controller={controller}
              assignmentTitle={slideDeckName}
            />
          ))
        }
      },
      { fireImmediately: true }
    )
    return () => disposer()
  }, [
    controller,
    cubit.assignmentId,
    cubit.roomStateId,
    cubit.sectionId,
    sessionProcessingOrEnded,
    showDialog,
    userId,
    userYetToCompletePeerReview,
    cubit.assignment.slideDeck.data.slideDeckName,
  ])
}
